import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { useUIStore } from '../../store/useUIStore';
import { useModules } from '../../lib/useModules';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../brand/Logo';

export default function Sidebar() {
    const { logout } = useAuthStore();
    const navigate = useNavigate();
    const { isSidebarCollapsed, toggleSidebar, setSidebarCollapsed } = useUIStore();

    const role = useAuthStore(state => state.user?.role);

    const isSuperAdmin = role === 'super_admin';
    const isManager = ['admin', 'manager'].includes(role);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const { isModuleEnabled } = useModules();

    // Cada entrada declara a qué módulo pertenece; el super admin las ve todas
    // porque no tiene tenant (useModules no le filtra nada).
    const agentItems = [
        { name: 'Mi Cartera', icon: 'person_pin', path: '/my-portfolio', module: 'portfolio' },
        { name: 'Leads AI', icon: 'bolt', path: '/leads', module: 'leads' },
        { name: 'Contactos', icon: 'group', path: '/contacts', fill: true, module: 'contacts' },
        { name: 'Mensajes', icon: 'chat', path: '/chat', module: 'chat' },
        { name: 'Tutor AI', icon: 'visibility', path: '/tutor', module: 'tutor' },
        { name: 'Emails', icon: 'mail', path: '/emails', module: 'emails' },
        { name: 'Cotizaciones', icon: 'request_quote', path: '/quotes', module: 'quotes' },
        { name: 'Plantillas', icon: 'description', path: '/settings/templates', module: 'templates' },
        { name: 'Negocios', icon: 'work', path: '/deals', module: 'deals' },
    ];

    const managerItems = [
        { name: 'Panel Manager', icon: 'bar_chart_4_bars', path: '/manager', module: 'dashboard' },
        { name: 'Leads AI', icon: 'bolt', path: '/leads', module: 'leads' },
        { name: 'Contactos', icon: 'group', path: '/contacts', fill: true, module: 'contacts' },
        { name: 'Mensajes', icon: 'chat', path: '/chat', module: 'chat' },
        { name: 'Tutor AI', icon: 'visibility', path: '/tutor', module: 'tutor' },
        { name: 'Emails', icon: 'mail', path: '/emails', module: 'emails' },
        { name: 'Cotizaciones', icon: 'request_quote', path: '/quotes', module: 'quotes' },
        // Solo en el menu de manager: configurar por donde entran los leads es
        // tarea de admin, y el agente no tiene nada que hacer aca.
        { name: 'Formularios', icon: 'dynamic_form', path: '/forms', module: 'forms' },
        { name: 'Campañas', icon: 'campaign', path: '/campaigns', module: 'campaigns' },
        { name: 'Usuarios', icon: 'manage_accounts', path: '/users', module: 'users' },
        { name: 'Plantillas', icon: 'description', path: '/settings/templates', module: 'templates' },
        { name: 'Sincronización', icon: 'sync_alt', path: '/settings/inventory', module: 'inventory' },
        { name: 'Sandbox IA', icon: 'science', path: '/ai-sandbox', module: 'sandbox' },
        { name: 'Negocios', icon: 'work', path: '/deals', module: 'deals' },
    ];

    const navItems = (isManager ? managerItems : agentItems)
        .filter(item => !item.module || isModuleEnabled(item.module));

    return (
        <motion.aside
            animate={{ width: isSidebarCollapsed ? 80 : 256 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={`hidden flex-col border-r border-gray-200 bg-background-light dark:bg-surface-dark dark:border-gray-800 lg:flex shrink-0 relative z-40 ${isSidebarCollapsed ? 'overflow-visible' : 'overflow-hidden'}`}
        >
            {/* Logo */}
            <div className={`flex items-center h-[73px] border-b border-gray-200 dark:border-gray-800 transition-all duration-300 ${isSidebarCollapsed ? 'px-0 justify-center' : 'px-6 gap-3'}`}>
                <div
                    onClick={() => isSidebarCollapsed ? setSidebarCollapsed(false) : navigate('/')}
                    className={`size-9 flex items-center justify-center rounded-lg bg-primary text-white shadow-sm shrink-0 cursor-pointer ${isSidebarCollapsed ? 'hover:scale-105 transition-transform' : ''}`}
                    title={isSidebarCollapsed ? "Expandir menú" : ""}
                >
                    <Logo className="size-7" />
                </div>

                <AnimatePresence>
                    {!isSidebarCollapsed && (
                        <motion.div
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: 'auto' }}
                            exit={{ opacity: 0, width: 0 }}
                            className="flex items-center overflow-hidden whitespace-nowrap flex-1"
                        >
                            <h2
                                className="text-slate-900 dark:text-slate-100 text-xl font-bold leading-tight tracking-tight cursor-pointer"
                                onClick={() => navigate('/')}
                            >
                                TornadoCRM
                            </h2>
                            <button
                                onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                                className="ml-auto flex items-center justify-center size-8 rounded-md text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">menu_open</span>
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Nav */}
            <nav className={`flex-1 ${isSidebarCollapsed ? 'overflow-visible' : 'overflow-y-auto'} ${isSidebarCollapsed ? 'px-2' : 'px-4'} py-6 space-y-1 custom-scrollbar`}>
                {/* ── Super Admin Section ── */}
                {isSuperAdmin && (
                    <>
                        <div className={`flex items-center px-3 mb-2 ${isSidebarCollapsed ? 'justify-center' : 'gap-2'}`}>
                            {!isSidebarCollapsed ? (
                                <>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-violet-500 dark:text-violet-400 whitespace-nowrap">
                                        Super Admin
                                    </span>
                                    <span className="flex-1 h-px bg-violet-200 dark:bg-violet-800/50" />
                                </>
                            ) : (
                                <span className="w-full h-px bg-violet-200 dark:bg-violet-800/50" />
                            )}
                        </div>
                        <NavLink
                            to="/super-admin/tenants"
                            className={({ isActive }) =>
                                `group relative flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 transition-colors ${isActive
                                    ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                                    : 'text-text-main hover:bg-violet-50 dark:text-gray-300 dark:hover:bg-violet-900/20'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <span className={`material-symbols-outlined ${isActive ? 'text-violet-600 dark:text-violet-400' : 'text-text-secondary'}`}>
                                        hub
                                    </span>
                                    <AnimatePresence>
                                        {!isSidebarCollapsed && (
                                            <motion.span
                                                initial={{ opacity: 0, width: 0 }}
                                                animate={{ opacity: 1, width: 'auto' }}
                                                exit={{ opacity: 0, width: 0 }}
                                                className="text-sm font-medium whitespace-nowrap overflow-hidden"
                                            >
                                                Tenants
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                    {isSidebarCollapsed && (
                                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-2xl border border-white/10 pointer-events-none">
                                            <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45 border-l border-b border-white/10"></div>
                                            Tenants
                                        </div>
                                    )}
                                </>
                            )}
                        </NavLink>

                        <NavLink
                            to="/super-admin/sync"
                            className={({ isActive }) =>
                                `group relative flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 transition-colors ${isActive
                                    ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                                    : 'text-text-main hover:bg-violet-50 dark:text-gray-300 dark:hover:bg-violet-900/20'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <span className={`material-symbols-outlined ${isActive ? 'text-violet-600 dark:text-violet-400' : 'text-text-secondary'}`}>
                                        sync_alt
                                    </span>
                                    <AnimatePresence>
                                        {!isSidebarCollapsed && (
                                            <motion.span
                                                initial={{ opacity: 0, width: 0 }}
                                                animate={{ opacity: 1, width: 'auto' }}
                                                exit={{ opacity: 0, width: 0 }}
                                                className="text-sm font-medium whitespace-nowrap overflow-hidden"
                                            >
                                                Sincronización
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                    {isSidebarCollapsed && (
                                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-2xl border border-white/10 pointer-events-none">
                                            <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45 border-l border-b border-white/10"></div>
                                            Sincronización
                                        </div>
                                    )}
                                </>
                            )}
                        </NavLink>
                        <div className="h-4" />
                        <div className={`flex items-center px-3 mb-2 ${isSidebarCollapsed ? 'justify-center' : 'gap-2'}`}>
                            {!isSidebarCollapsed ? (
                                <>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                                        Sistema
                                    </span>
                                    <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                                </>
                            ) : (
                                <span className="w-full h-px bg-gray-200 dark:bg-gray-700" />
                            )}
                        </div>
                    </>
                )}

                {/* ── Regular Nav ── (hidden for super_admin which has no tenant) */}
                {!isSuperAdmin && navItems.map((item) => (
                    <NavLink
                        key={item.name}
                        to={item.path}
                        className={({ isActive }) =>
                            `group relative flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 transition-colors ${isActive
                                ? 'bg-primary/10 text-primary'
                                : 'text-text-main hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined ${!isActive ? 'text-text-secondary' : ''} ${item.fill && isActive ? 'fill-1' : ''}`}>
                                    {item.icon}
                                </span>
                                <AnimatePresence>
                                    {!isSidebarCollapsed && (
                                        <motion.span
                                            initial={{ opacity: 0, width: 0 }}
                                            animate={{ opacity: 1, width: 'auto' }}
                                            exit={{ opacity: 0, width: 0 }}
                                            className="text-sm font-medium whitespace-nowrap overflow-hidden"
                                        >
                                            {item.name}
                                        </motion.span>
                                    )}
                                </AnimatePresence>

                                {/* Tooltip */}
                                {isSidebarCollapsed && (
                                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-2xl border border-white/10 pointer-events-none">
                                        <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45 border-l border-b border-white/10"></div>
                                        {item.name}
                                    </div>
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>


            {/* Footer */}
            <div className={`border-t border-gray-200 dark:border-gray-800 space-y-1 ${isSidebarCollapsed ? 'p-2' : 'p-4'}`}>
                {!isSuperAdmin && (
                    <NavLink
                        to="/settings"
                        className={`group relative flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 text-text-main hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors`}
                    >
                        <span className="material-symbols-outlined text-text-secondary">settings</span>
                        <AnimatePresence>
                            {!isSidebarCollapsed && (
                                <motion.span
                                    initial={{ opacity: 0, width: 0 }}
                                    animate={{ opacity: 1, width: 'auto' }}
                                    exit={{ opacity: 0, width: 0 }}
                                    className="text-sm font-medium whitespace-nowrap overflow-hidden"
                                >
                                    Configuración
                                </motion.span>
                            )}
                        </AnimatePresence>
                        {isSidebarCollapsed && (
                            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-2xl border border-white/10 pointer-events-none">
                                <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45 border-l border-b border-white/10"></div>
                                Configuración
                            </div>
                        )}
                    </NavLink>
                )}

                <button
                    onClick={handleLogout}
                    className={`group relative w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors`}
                >
                    <span className="material-symbols-outlined">logout</span>
                    <AnimatePresence>
                        {!isSidebarCollapsed && (
                            <motion.span
                                initial={{ opacity: 0, width: 0 }}
                                animate={{ opacity: 1, width: 'auto' }}
                                exit={{ opacity: 0, width: 0 }}
                                className="text-sm font-medium whitespace-nowrap overflow-hidden"
                            >
                                Cerrar sesión
                            </motion.span>
                        )}
                    </AnimatePresence>
                    {isSidebarCollapsed && (
                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-2xl border border-white/10 pointer-events-none">
                            <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-red-600 rotate-45 border-l border-b border-white/10"></div>
                            Cerrar sesión
                        </div>
                    )}
                </button>
            </div>
        </motion.aside>
    );
}
